import {
  useState,
  useContext,
  createContext,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { NavbarUser } from "@ucchub/ui/src/types";
import { AuthenticationService, ViewType } from "../types";

interface IProfileModal {
  setView: Dispatch<SetStateAction<ViewType>>;
  view: ViewType;
  isShown: boolean;
  toggle: () => void;
  navbarUser: NavbarUser;
  setNavbarUser: Dispatch<SetStateAction<NavbarUser>>;
  authenticationService: AuthenticationService | null;
  baseUrl: string;
}

// Create a new context with default values
const ProfileModalContext = createContext<IProfileModal>({
  setView: () => null,
  view: "VIEW_PROFILE",
  isShown: false,
  toggle: () => null,
  navbarUser: {
    name: "",
    email: "",
    initials: "",
    role: "",
  },
  setNavbarUser: () => null,
  authenticationService: null,
  baseUrl: "",
});

const useProvideProfileModal = () => {
  // Local state for the state hook
  const [view, setView] = useState<IProfileModal["view"]>("VIEW_PROFILE");
  const [isShown, setIsShown] = useState(false);
  const [navbarUser, setNavbarUser] = useState<NavbarUser>({
    name: "",
    email: "",
    initials: "",
    role: "",
  });

  // Return all the available hook functions / values
  return {
    view,
    setView,
    isShown,
    toggle: () => setIsShown(!isShown),
    navbarUser,
    setNavbarUser,
    authenticationService: null,
    baseUrl: "",
  };
};

// Creates a provider which is sent to Context.tsx
export const ProfileModalProvider = ({
  children,
  authenticationService,
  baseUrl,
}: {
  children?: ReactNode;
  authenticationService: AuthenticationService;
  baseUrl: string;
}) => {
  const data: IProfileModal = useProvideProfileModal();

  return (
    <ProfileModalContext.Provider
      value={{ ...data, authenticationService, baseUrl }}
    >
      {children}
    </ProfileModalContext.Provider>
  );
};

// Export the hook to be used in other components
export const useProfileModal = () => useContext(ProfileModalContext);
